import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const POINTS_SL = [
  {
    label: 'Enotna vstopna točka (SPOC)',
    desc: 'Naročnik (PML) komunicira le z eno osebo, ki ima celosten pregled nad operacijami, proračunom in rezultati.',
  },
  {
    label: 'Informacijsko vozlišče',
    desc: 'Projektni vodja razpolaga z vsemi podatki s terena v realnem času. Ni informacijskega šuma – vsaka situacija je takoj procesirana.',
  },
  {
    label: 'Upravljanje KPI-jev',
    desc: 'Aktivno spremljanje doseganja ciljev (20 DCC/izmeno, 15 lokacij/dan). Vodja ne le poroča, ampak proaktivno prilagaja strategijo.',
  },
  {
    label: 'Strateško svetovanje',
    desc: 'Prenos surovih podatkov s terena v uporabne Business Intelligence uvide za PML — trendi potrošnikov, odzivi na portfelj.',
  },
  {
    label: 'Varuh standardov',
    desc: 'Zagotavlja, da so vsi procesi (od videza informatorjev do komunikacijskih skript) 100 % skladni s Philip Morris standardi in RSP načeli.',
  },
];

const POINTS_EN = [
  {
    label: 'Single Point of Contact (SPOC)',
    desc: 'The client (PML) communicates with one person only, who holds a complete overview of operations, budget and results.',
  },
  {
    label: 'Information hub',
    desc: 'The project manager has access to all field data in real time. No information noise — every situation is processed immediately.',
  },
  {
    label: 'KPI management',
    desc: 'Active monitoring of target achievement (20 DCC/shift, 15 locations/day). The manager not only reports but proactively adjusts strategy.',
  },
  {
    label: 'Strategic consulting',
    desc: 'Converting raw field data into actionable Business Intelligence insights for PML — consumer trends, portfolio responses.',
  },
  {
    label: 'Standards guardian',
    desc: 'Ensures all processes (from informer appearance to communication scripts) are 100% compliant with Philip Morris standards and RSP principles.',
  },
];

const ACCENT = '#C87533';

export const SpocSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const POINTS = lang === 'sl' ? POINTS_SL : POINTS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.fromTo('.spoc-left',
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.7, ease: 'power3.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true } }
      );
      gsap.fromTo('.spoc-point',
        { opacity: 0, x: 24 },
        {
          opacity: 1, x: 0, duration: 0.55, ease: 'power3.out', stagger: 0.1,
          scrollTrigger: { trigger: '.spoc-right', start: 'top 75%', once: true },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="spoc"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Badge */}
        <div className="spoc-left mb-10">
          <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
              {lang === 'sl' ? 'Projektno vodenje' : 'Project Leadership'}
            </span>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-16 items-start">

            {/* Left — title block */}
            <div className="flex flex-col gap-5">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {lang === 'sl'
                  ? <><span style={{ color: ACCENT }}>Projektni vodja</span><br />Vaš SPOC</>
                  : <><span style={{ color: ACCENT }}>Project Manager</span><br />Your SPOC</>
                }
              </h2>
              <p className="text-white/45 text-base leading-relaxed max-w-xs">
                {lang === 'sl'
                  ? 'Ena oseba, 100 % informacij, polna odgovornost.'
                  : 'One person, 100% information, full accountability.'}
              </p>

              {/* Decorative metric */}
              <div
                className="mt-2 inline-flex flex-col items-start gap-1 px-5 py-4 rounded-2xl border"
                style={{ borderColor: `${ACCENT}30`, background: `${ACCENT}08` }}
              >
                <span className="font-display text-4xl font-bold" style={{ color: ACCENT }}>1</span>
                <span className="font-mono text-xs text-white/40 uppercase tracking-widest">
                  {lang === 'sl' ? 'kontaktna oseba' : 'contact person'}
                </span>
              </div>
            </div>

            {/* Right — points */}
            <div className="spoc-right flex flex-col gap-4">
              {POINTS.map((pt, i) => (
                <div
                  key={i}
                  className="spoc-point group relative rounded-xl border border-white/7 bg-white/3 backdrop-blur-sm px-5 py-4 flex gap-4 hover:border-white/13 hover:bg-white/5 transition-all duration-300"
                >
                  {/* Left accent bar */}
                  <div
                    className="flex-shrink-0 w-0.5 rounded-full self-stretch"
                    style={{ background: `linear-gradient(180deg, ${ACCENT}80, ${ACCENT}10)` }}
                  />
                  <div>
                    <span className="text-white/85 text-sm font-semibold">{pt.label}: </span>
                    <span className="text-white/45 text-sm leading-relaxed">{pt.desc}</span>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};
